import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  COACH_PROMPT_CATEGORY_HINTS,
  COACH_PROMPT_CATEGORY_LABELS,
  type CoachPromptCategory,
} from '@mlm/shared/coaching';
import { Chip, ErrorNote, Loading, Screen } from '../components/primitives';
import { MoreWithPlan, ProLock } from '../components/pro-lock';
import { coaching, type CoachPrompt } from '../lib/api';
import { useCoachPrompts, useFeature } from '../lib/queries';

/**
 * The prompt library: ready-made things to ask Master Kala, by category. A
 * tap fills the composer of the thread the member came from (or opens a new
 * one) and sends NOTHING, so the [brackets] get filled in first. The gate is
 * silent on the server; here it hides the screen when the tenant switched the
 * library off and shows the lock in place when a higher plan opens it.
 */
export function CoachPromptsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/coach';
  const state = useFeature('prompt_library');
  const library = useCoachPrompts();
  const [category, setCategory] = useState<CoachPromptCategory | null>(null);

  if (state === 'off') return <Navigate to="/coach" replace />;
  if (state === 'locked') {
    return (
      <Screen back={from} eyebrow="Prompts" title="What do you want to ask?">
        <ProLock feature="prompt_library" />
      </Screen>
    );
  }
  if (library.isPending) return <Loading />;
  if (library.isError)
    return <ErrorNote error={library.error} retry={() => void library.refetch()} />;

  const { prompts, categories, proOnlyCount } = library.data;
  const offered = categories.filter((c) => prompts.some((p) => p.category === c));
  const shown = category ? prompts.filter((p) => p.category === category) : prompts;

  const pick = (p: CoachPrompt) => {
    coaching.event('coach_prompt_used', { prompt: p.id, category: p.category });
    // Back to the thread (or the Coach tab, which opens one) with the draft in
    // router state; replace, so Back from the thread skips this screen.
    navigate(from, { replace: true, state: { draft: p.body } });
  };

  return (
    <Screen
      back={from}
      eyebrow="Prompts"
      title="What do you want to ask?"
      subtitle="Tap one and it lands in your message box, ready to edit. Fill in the [brackets] before you send."
    >
      <div className="flex flex-wrap gap-2 border-b border-border px-5 py-5">
        <Chip selected={category === null} onClick={() => setCategory(null)}>
          All
        </Chip>
        {offered.map((c) => (
          <Chip key={c} selected={category === c} onClick={() => setCategory(c)}>
            {COACH_PROMPT_CATEGORY_LABELS[c]}
          </Chip>
        ))}
      </div>
      {category && (
        <p className="border-b border-border px-5 py-3 text-sm leading-relaxed text-ink/70">
          {COACH_PROMPT_CATEGORY_HINTS[category]}
        </p>
      )}
      {shown.map((p) => (
        <PromptRow key={p.id} prompt={p} showCategory={category === null} onPick={pick} />
      ))}
      {shown.length === 0 && (
        <p className="px-5 py-6 text-sm text-muted-foreground">Nothing here yet.</p>
      )}
      <MoreWithPlan count={proOnlyCount} noun="prompt" />
    </Screen>
  );
}

/** Title, the category on the ALL view, and the prompt itself in readable type (not the mono eyebrow a Row's meta uses). */
function PromptRow({
  prompt,
  showCategory,
  onPick,
}: {
  prompt: CoachPrompt;
  showCategory: boolean;
  onPick: (p: CoachPrompt) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(prompt)}
      className="flex w-full items-start gap-3.5 border-b border-border px-5 py-4 text-left transition-colors hover:bg-surface"
    >
      <span className="min-w-0 flex-1">
        {showCategory && (
          <span className="eyebrow mb-1 block">
            {COACH_PROMPT_CATEGORY_LABELS[prompt.category]}
          </span>
        )}
        <span className="block text-[0.95rem] leading-snug text-ink">{prompt.title}</span>
        <span className="mt-1 block text-sm leading-relaxed text-ink/70">{prompt.body}</span>
      </span>
      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-sage" aria-hidden />
    </button>
  );
}
